import React, { useEffect, useState } from 'react';
import {
  getBusinessArea,
  getCurrentFile,
  getCurrentSummary,
  getRegenerateExtraction,
  getSelectedBusinessUnit,
  getService,
  setCurrentFile,
  setCurrentSummary,
  setGeneratingExtraction,
  setRegenerateExtraction,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import config from 'config';
import { useNavigate } from 'react-router-dom';
import styles from '../Summary/Summary.module.scss';
import { Button } from '@zurich-primitives/button';
import { Link } from '@zurich-data-dsm-icons/action.link';
import { getFileExtraction } from 'integration';
import {
  generateCSV,
  IExtractionResult,
  resetFieldsValues,
  setFieldsValues
} from 'app/utils';
import {
  DownloadCSVButton,
  GeneratingExtraction,
  SummaryField
} from 'app/components';
import { SERVICE_NAMES, ServicesData } from 'app/utils/consts';
import {
  capitalize,
  transformExtractionResult,
  transformText
} from 'app/utils/functions';

export const InvoiceExtractorSummary: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const currentFile = useAppSelector(getCurrentFile);
  const regenerateExtraction = useAppSelector(getRegenerateExtraction);
  const currentSummary = useAppSelector(getCurrentSummary);
  const selectedBusinessUnit = useAppSelector(getSelectedBusinessUnit);

  const [fields, setFields] = useState<
    Array<{ query: string; value?: string }>
  >([]);
  const [originalExtraction, setOriginalExtraction] =
    useState<IExtractionResult>();
  const [currentTitle, setCurrentTitle] = useState<string>('');

  useEffect(() => {
    dispatch(setRegenerateExtraction(null));
  }, [currentBusinessArea, currentService]);

  const fetchInvoiceExtractor = ({ file }: { file: File }): void => {
    dispatch(setGeneratingExtraction(true));
    const body = new FormData();
    body.append('file', file, file.name);
    if (selectedBusinessUnit !== 'default')
      body.append('businessUnit', selectedBusinessUnit);
    setCurrentSummary(undefined);
    getFileExtraction(String(currentBusinessArea), String(currentService), body)
      .then((response) => {
        onInvoiceExtractorResponse(response);
      })
      .catch(() => {
        resetFieldsValues();
        dispatch(setCurrentFile(undefined));
        navigate(config.APP.PATH.LANDING);
      })
      .finally(() => {
        dispatch(setRegenerateExtraction(false));
        dispatch(setGeneratingExtraction(false));
      });
  };

  const mapEntries = (objectInfo: any): any => {
    return Object.entries(objectInfo)
      .map(([key, value]) => `${String(capitalize(key))}: ${String(value)}  `)
      .join('\n');
  };

  const onInvoiceExtractorResponse = (response: any): void => {
    const currentSummary: IExtractionResult = {};
    const responseFields = [];

    for (const field in response.fields) {
      const value = response.fields[field];
      const formattedField = transformText(field);

      if (Array.isArray(value)) {
        const stringResult = value
          .map((objectInfo: any) => {
            return mapEntries(objectInfo);
          })
          .join('\n\n');

        currentSummary[formattedField] = { value: String(stringResult) };
      } else if (typeof value === 'object') {
        const stringResult = Object.entries(value)
          .map((objectInfo: [string, any]) => {
            return `${transformText(objectInfo[0])}: ${String(objectInfo[1])}`;
          })
          .join('\n\n');

        currentSummary[formattedField] = { value: String(stringResult) };
      } else {
        currentSummary[formattedField] = { value };
      }
      responseFields.push({ query: formattedField, value });
    }
    dispatch(setCurrentSummary(currentSummary));
    setFields(responseFields);
    setOriginalExtraction(currentSummary);
  };

  const handleOpenAPI = (): void => {
    currentService !== undefined &&
      navigate(config.APP.PATH.API.replace(':api', currentService));
  };

  useEffect(() => {
    if (
      currentFile &&
      currentService === SERVICE_NAMES.INVOICE_EXTRACTOR &&
      (regenerateExtraction || regenerateExtraction === null)
    ) {
      fetchInvoiceExtractor({
        file: currentFile
      });
    }
    if (!currentFile) resetFieldsValues();
  }, [currentFile, regenerateExtraction]);

  useEffect(() => {
    if (originalExtraction) {
      setFieldsValues(originalExtraction);
    } else {
      resetFieldsValues();
    }
  }, [originalExtraction]);

  return (
    <div className={styles['summary-section']}>
      <div className={styles.header}>
        <h3>
          {currentService
            ? ServicesData[currentService].SummaryTitle
            : 'Summary'}
        </h3>
        <Button onClick={handleOpenAPI} variant="tertiary">
          <Button.Icon>
            <Link />
          </Button.Icon>
          <Button.Label>Use our API version</Button.Label>
        </Button>
      </div>

      {fields.map((field) => (
        <SummaryField
          key={`field-${field.query}`}
          id={`field-${field.query}`}
          title={field.query}
          currentTitle={currentTitle}
          setCurrentTitle={setCurrentTitle}
        />
      ))}

      {currentService !== undefined &&
        ServicesData[currentService].ExtractionType === 'extraction' && (
          <DownloadCSVButton
            handleOnClick={() =>
              currentFile &&
              currentSummary &&
              generateCSV(
                transformExtractionResult(currentSummary as IExtractionResult),
                currentFile?.name
              )
            }
            isDisabled={originalExtraction === undefined}
          />
        )}
      <GeneratingExtraction />
    </div>
  );
};
